import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { usePrintStore } from '@renderer/stores/printStore'
import { initiatePrint } from '@renderer/utils/pipeline/printService'
import cogoToast from '@successtar/cogo-toast'
import Preview from '@renderer/components/Preview'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { imageUtils } from '@renderer/utils/imageUtils'
import type { Filter as FilterDoc } from '@renderer/types/configState'
import { BK_CAPTURE_RATIO } from './cutCategories'
import BkScreen from './BkScreen'
import BkPixel, { BK_INK } from './BkPixel'
import { useBkPageTimeout } from './pageTimeout'
import BkTimer from './BkTimer'
import { displayName, scriptWord } from './script'
import { useFilterThumbs } from './useFilterThumbs'
import { useCameraRecovery } from '@renderer/pages/View'
import { useCameraStore } from '@renderer/stores/cameraStore'
import {
  LIVE_VIEW_FIRST_FRAME_GRACE_MS,
  framesResumedDuringRecovery,
  watchdogStep
} from './liveViewWatchdog'
import { composeFilterSheet } from './filterSheet'
import {
  BK_ASSET,
  BK_BIG_FRAME,
  BK_FILTER_LABEL,
  BK_FILTER_PLAQUE,
  BK_FILTER_WALL,
  BK_HIT,
  bkHit
} from './layout'

/** The label's photo, above the name. */
const THUMB = { width: BK_FILTER_LABEL.width, height: 62 }

/** Whether the store holds any device for this camera slot (a parked one counts). */
function hasCameraFor(cameraKey: 'primary' | 'secondary'): boolean {
  const store = useCameraStore.getState()
  return cameraKey === 'secondary'
    ? !!(store.secondaryCamera || store.secondaryWebcam)
    : !!(store.camera || store.webcam)
}

/**
 * Screen 4 — "choose filter": a gallery wall of up to eight.
 *
 * Each of the plate's hanging plaques shows the guest, live, through one
 * filter, with its name stitched underneath; the gold frame shows the chosen
 * one full size. Tap a plaque to choose, the start swan to go on.
 *
 * The filters are the project's own, in the order the Studio lists them, so
 * the operator adds, renames and reorders them there. The first is the
 * default.
 */
const Filter: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, uploadSessionState, logActivity } = useSessionStateStore()
  const { filters, config } = useConfigStore()
  const selectedCamera = useSessionStateStore((s) => s.userPreferences?.selectedCamera)
  const cameraKey = (selectedCamera as 'primary' | 'secondary') ?? 'primary'
  const { cameraSettings, secondaryCameraSettings } = useSettingsStore()
  const camera = cameraKey === 'secondary' ? secondaryCameraSettings : cameraSettings

  /** The Studio's order: filterIds first, anything else the catalogue sent after. */
  const ordered: FilterDoc[] = useMemo(() => {
    const ids: string[] = (config?.pages?.filter?.settings?.filterIds ?? []).map(String)
    const byId = new Map(filters.map((f) => [f._id, f]))
    const inOrder = ids.map((id) => byId.get(id)).filter((f): f is FilterDoc => Boolean(f))
    return inOrder.length ? inOrder : filters
  }, [config, filters])
  const wall = useMemo(() => ordered.slice(0, BK_FILTER_WALL.length), [ordered])
  /**
   * Filters listed after the wall's eight. Guests never see them; they are
   * candidates an operator can print (Ctrl+Shift+G) to compare on paper
   * before swapping one onto the wall in the Studio.
   */
  const candidates = useMemo(() => ordered.slice(BK_FILTER_WALL.length), [ordered])

  const [chosenId, setChosenId] = useState<string | null>(null)
  const chosen = wall.find((f) => f._id === chosenId) ?? wall[0]

  /**
   * The big preview's own frames, kept for the wall. Wrapping the preview's
   * fetch means the thumbnails cost the camera nothing: no second stream, no
   * extra calls into a DSLR that can only answer one at a time.
   */
  const latestFrame = useRef<Blob | null>(null)
  /** When the live view last delivered a frame; see the watchdog below. */
  const lastFrameAt = useRef(0)
  /** When the watchdog last acted (or the page opened, plus the first-frame grace). */
  const lastActionAt = useRef(Date.now() + LIVE_VIEW_FIRST_FRAME_GRACE_MS)
  const capturePreview = useCallback(
    async (
      key: 'primary' | 'secondary',
      options?: Parameters<typeof unifiedCameraUtils.capturePreview>[1]
    ): Promise<Blob | undefined> => {
      const blob = await unifiedCameraUtils.capturePreview(key, options)
      if (blob) {
        latestFrame.current = blob
        lastFrameAt.current = Date.now()
      }
      return blob
    },
    []
  )

  /**
   * The live view watchdog (liveViewWatchdog.ts): a stalled preview is
   * restarted, and a second stall goes to the booth's camera recovery prompt
   * while the session — and the guest's payment — stays put. The countdown is
   * held during recovery so a dead camera never auto-starts capture.
   */
  const cameraRecovery = useCameraRecovery()
  const [previewKey, setPreviewKey] = useState(0)
  const [recovering, setRecovering] = useState(false)
  const recoveringRef = useRef(false)
  const recoveryStartedAt = useRef(0)
  const strikesRef = useRef(0)
  useEffect(() => {
    /** Back to watching: a fresh grace, no strikes, countdown running again. */
    const endRecovery = (): void => {
      strikesRef.current = 0
      lastActionAt.current = Date.now() + LIVE_VIEW_FIRST_FRAME_GRACE_MS
      recoveringRef.current = false
      setRecovering(false)
    }
    const timer = setInterval(() => {
      const now = Date.now()
      if (recoveringRef.current) {
        if (framesResumedDuringRecovery(lastFrameAt.current, recoveryStartedAt.current)) {
          logActivity('BK live view came back while the camera prompt was up')
          endRecovery()
        }
        return
      }
      const cameraPresent = hasCameraFor(cameraKey)
      const { action, strikes } = watchdogStep(
        now,
        lastFrameAt.current,
        lastActionAt.current,
        strikesRef.current,
        { cameraPresent }
      )
      strikesRef.current = strikes
      if (action === 'restart' || (action === 'recover' && !cameraRecovery)) {
        logActivity('BK live view stalled: restarting the preview', { level: 'warn' })
        lastActionAt.current = now
        setPreviewKey((k) => k + 1)
      } else if (action === 'recover' && cameraRecovery) {
        logActivity(
          cameraPresent
            ? 'BK live view stalled again: asking to reconnect the camera'
            : 'BK live view has no camera: asking to reconnect it',
          { level: 'error' }
        )
        recoveringRef.current = true
        recoveryStartedAt.current = now
        setRecovering(true)
        cameraRecovery.onCameraRecoveryRequest(() => {
          logActivity('BK camera reconnected on the filter page')
          endRecovery()
          setPreviewKey((k) => k + 1)
        })
      }
    }, 1000)
    return (): void => clearInterval(timer)
  }, [cameraRecovery, cameraKey, logActivity])
  const thumbs = useFilterThumbs(wall, latestFrame, THUMB)
  // Same turn and mirror as the big preview, which applies them in CSS.
  const faceTransform = [
    camera?.previewRotation ? `rotate(${camera.previewRotation}deg)` : '',
    imageUtils.getRotationAwareMirrorTransform(camera?.previewRotation ?? 0, camera?.mirror ?? true)
  ]
    .filter(Boolean)
    .join(' ')

  /**
   * The operator's filter test sheets: Ctrl+Shift+F prints the camera's current
   * frame through every filter on the wall, Ctrl+Shift+G through the candidate
   * filters listed after it, on one 6x8 sheet each. They are for setup over
   * remote support, which has a keyboard; a guest at the touch screen cannot
   * press them. Test prints, not session prints: logged as such, and never
   * counted against the guest's sheet.
   */
  const frameId = useSessionStateStore((s) => s.userPreferences?.frameId)
  const sheetBusyRef = useRef(false)
  const printFilterSheet = useCallback(async (which: 'wall' | 'candidates'): Promise<void> => {
    if (sheetBusyRef.current) return
    const onSheet = which === 'wall' ? wall : candidates
    if (!onSheet.length) {
      cogoToast.warn('No candidate filters: list them after the eight wall filters in the Studio')
      return
    }
    const frame = latestFrame.current
    if (!frame) {
      cogoToast.warn('No camera frame yet: wait for the live view, then try again')
      return
    }
    sheetBusyRef.current = true
    try {
      const sheet = await composeFilterSheet(frame, onSheet)
      const { printerName, settings } = usePrintStore.getState().getPrinterForFrame(frameId || '')
      logActivity(`BK filter test sheet sent (${which})`, { filters: onSheet.length, printerName })
      await initiatePrint(sheet, printerName, 1, settings, navigator.onLine, undefined, 'test_print', {
        projectId: projectId || undefined
      })
      cogoToast.success('Filter test sheet sent to the printer')
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      logActivity(`BK filter test sheet failed: ${message}`, { level: 'error' })
      cogoToast.error(`Filter test sheet failed: ${message}`)
    } finally {
      sheetBusyRef.current = false
    }
  }, [candidates, frameId, logActivity, projectId, wall])

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (!event.ctrlKey || !event.shiftKey) return
      const key = event.key.toLowerCase()
      if (key !== 'f' && key !== 'g') return
      event.preventDefault()
      void printFilterSheet(key === 'f' ? 'wall' : 'candidates')
    }
    window.addEventListener('keydown', onKey)
    return (): void => window.removeEventListener('keydown', onKey)
  }, [printFilterSheet])

  const choose = (filter: FilterDoc): void => {
    setChosenId(filter._id)
    setSessionState({ userPreferences: { filterId: filter._id } })
    logActivity(`BK filter previewed: ${filter.name}`)
  }

  const start = (): void => {
    // Never leave for capture without a look committed: the first on the wall
    // is the default.
    const filterId = chosen?._id ?? null
    setSessionState({ userPreferences: { filterId } })
    logActivity('BK filter confirmed', { to: 'capture', filter: chosen?.name })
    uploadSessionState()
    navigate(`/${projectId}/capture`)
  }

  const remaining = useBkPageTimeout(
    config?.pages?.filter?.settings?.filterTimeout,
    start,
    !recovering
  )

  return (
    <BkScreen plate="filter-wall.webp">
      {/* Live camera inside the painted gold frame, in the chosen look. */}
      <div style={bkHit(BK_BIG_FRAME)} className="overflow-hidden">
        <Preview
          key={previewKey}
          cameraKey={cameraKey}
          capturePreview={capturePreview}
          ratio={BK_CAPTURE_RATIO}
          adjustments={chosen?.adjustments}
          postLUTAdjustments={chosen?.postLUTAdjustments}
          filterOverlay={chosen?.overlay}
          filter={chosen?._id}
          fillContainer
        />
      </div>

      {/* The wall. Drawn bottom row first, so each ribbon runs up behind the
          plaque above it. */}
      {wall
        .map((filter, i) => ({ filter, i }))
        .reverse()
        .map(({ filter, i }) => {
          const at = BK_FILTER_WALL[i]
          const on = filter._id === chosen?._id
          return (
            <button
              key={filter._id}
              aria-label={`${displayName(filter.name)} filter`}
              aria-pressed={on}
              onClick={() => choose(filter)}
              className="absolute cursor-pointer bg-transparent p-0"
              style={{ left: at.left, top: at.top, ...BK_FILTER_PLAQUE }}
            >
              <img
                src={BK_ASSET('filter-plaque.webp')}
                alt=""
                aria-hidden
                className="pointer-events-none absolute inset-0 h-full w-full"
              />
              <span
                className="absolute flex flex-col overflow-hidden bg-[#EEE8D6]"
                style={bkHit(BK_FILTER_LABEL)}
              >
                <span className="block shrink-0 overflow-hidden" style={THUMB}>
                  {thumbs[filter._id] && (
                    <img
                      src={thumbs[filter._id]}
                      alt=""
                      aria-hidden
                      className="h-full w-full object-cover"
                      style={{ transform: faceTransform || undefined }}
                    />
                  )}
                </span>
                {/* The chosen one's name is inked in, as the chosen print
                    note is on the payment page. */}
                <span
                  className="flex flex-1 items-center justify-center"
                  style={{ background: on ? BK_INK : undefined }}
                >
                  <BkPixel
                    rows={scriptWord(displayName(filter.name))}
                    block={2}
                    color={on ? '#FFFAF9' : undefined}
                  />
                </span>
              </span>
            </button>
          )
        })}

      {/* Painted "start" swan under the preview frame. */}
      <button
        aria-label="Start capture"
        onClick={start}
        style={bkHit(BK_HIT.filter.start)}
        className="cursor-pointer bg-transparent"
      />
      <BkTimer seconds={remaining} />
    </BkScreen>
  )
}

export default Filter
